import { type ComponentType, type ReactNode, useCallback, useEffect, useState } from "react";
import { match, P } from "ts-pattern";
import { FullscreenSpinner, YStack } from "@medbillai/ui";
import { back, createFlow, type FlowState, type FlowTransition, type OnDone, type Outcomes, type ScreenKey, type TaggedUnion, type TransitionTable, tagged, useBrowserHistorySync, useNavigableFlow } from "../../lib/flow-engine";
import { Education, type EducationOutcomes } from "./Education";
import { type IntentOutcomes, IntentScreen } from "./Intent";
import { Notifications, type NotificationsOutcomes } from "./Notifications";
import { Resuming, type ResumingOutcomes } from "./Resuming";
import type { Intent, IntentData, ResolvedIntentData } from "./types";
import { ValueProps, type ValuePropsOutcomes } from "./ValueProps";
export const ONBOARDING_VERSION = "v2.0" as const;
export type OnboardingConfig = {
  ValueProps: {
    needs: {};
    outcomes: ValuePropsOutcomes;
  };
  Intent: {
    needs: {};
    outcomes: IntentOutcomes;
  };
  Education: {
    needs: IntentData;
    outcomes: EducationOutcomes;
  };
  Resuming: {
    needs: {};
    outcomes: ResumingOutcomes;
  };
  Notifications: {
    needs: ResolvedIntentData;
    outcomes: NotificationsOutcomes;
  };
  Complete: {
    needs: {};
    outcomes: never;
  };
};
export type OnboardingContext = {
  isWeb: boolean;
};
export const Screen = createFlow<OnboardingConfig>();

// Note: For auth handoff outcomes, we stay on the current screen to enable
// a seamless URL navigation animation.
const transitions: TransitionTable<OnboardingConfig, OnboardingContext> = {
  ValueProps: {
    logIn: () => Screen.ValueProps(),
    // Noop: Triggers exitToLogin onHandoff
    signUp: () => Screen.Intent(),
    back: () => back()
  },
  Intent: {
    selected: ({
      intent
    }) => Screen.Education({
      intent
    }),
    back: () => back()
  },
  Education: {
    continue: () => Screen.Education(),
    // Noop: Triggers awaitingAccount onHandoff
    back: () => back()
  },
  Resuming: {
    ready: ({
      intent
    }, {
      isWeb
    }) => isWeb ? Screen.Complete() : Screen.Notifications({
      intent
    })
  },
  Notifications: {
    configured: () => Screen.Complete(),
    skipped: () => Screen.Complete()
  },
  Complete: {}
};
type AuthHandoffShapes = {
  awaitingAccountCreation: {
    intent: Intent;
  };
  exitToLogin: {};
};
export const AuthHandoff = tagged<AuthHandoffShapes>();
export type AuthHandoff = TaggedUnion<AuthHandoffShapes>;
export type OnboardingTransitionProps = {
  stateKey: string;
  direction: 1 | -1;
  children: ReactNode;
};
export type OnboardingFlowProps = {
  initial: FlowState<OnboardingConfig>;
  ctx: OnboardingContext;
  onComplete: () => void;
  onAuthHandoff: OnDone<AuthHandoffShapes>;
  onTransition?: (event: FlowTransition<OnboardingConfig>) => void;
  /** Fires with the committed state on first render and after every
   * transition — a render cycle after `onTransition`. */
  onStateLoaded?: (state: FlowState<OnboardingConfig>) => void;
  /**
   * Screen-transition wrapper — supplied per-platform by the call site so
   * each platform uses the right animation tech (framer-motion on web,
   * Tamagui+Reanimated on native).
   */
  Transition: ComponentType<OnboardingTransitionProps>;
};
export function OnboardingFlow({
  initial,
  ctx,
  onAuthHandoff,
  onComplete,
  onTransition: parentOnTransition,
  onStateLoaded,
  Transition
}: OnboardingFlowProps) {
  const onTransition = useCallback((event: FlowTransition<OnboardingConfig>) => {
    // Observability sees every outcome, including the auth-handoff ones the
    // match below routes out of the flow.
    parentOnTransition?.(event);
    match([event.from, event.outcome]).with([{
      tag: "ValueProps"
    }, {
      tag: "logIn"
    }], () => {
      onAuthHandoff(AuthHandoff.exitToLogin());
    }).with([{
      tag: "Education",
      data: {
        intent: P.select()
      }
    }, {
      tag: "continue"
    }], intent => {
      onAuthHandoff(AuthHandoff.awaitingAccountCreation({
        intent
      }));
    }).otherwise(() => {});
  }, [parentOnTransition, onAuthHandoff]);
  const [direction, setDirection] = useState<1 | -1>(1);
  const {
    state,
    bindOnDone: engineBindOnDone,
    canGoBack,
    goBack: engineGoBack
  } = useNavigableFlow<OnboardingConfig, OnboardingContext>({
    transitions,
    initial,
    ctx,
    onTransition,
    onStateLoaded
  });
  useEffect(() => {
    if (state.tag === "Complete") {
      onComplete();
    }
  }, [state.tag, onComplete]);

  // setState in this handler flushes synchronously when the handler returns,
  // re-rendering the outgoing screen with the new direction's `exit` prop.
  // The rAF then fires the engine dispatch in a separate batch, so when
  // AnimatePresence captures the exiting child's `exit`, direction has
  // already settled.
  const bindOnDone = <K extends ScreenKey<OnboardingConfig>,>(tag: K) => (outcome: TaggedUnion<Outcomes<OnboardingConfig, K>>) => {
    setDirection(outcome.tag === "back" ? -1 : 1);
    requestAnimationFrame(() => {
      engineBindOnDone(tag)(outcome);
    });
  };
  const goBack = () => {
    setDirection(-1);
    requestAnimationFrame(() => {
      engineGoBack();
    });
  };
  useBrowserHistorySync({
    stateTag: state.tag,
    canGoBack,
    goBack
  });
  const screen = match(state).with({
    tag: "ValueProps"
  }, () => <ValueProps onDone={bindOnDone("ValueProps")} />).with({
    tag: "Intent"
  }, () => <IntentScreen onDone={bindOnDone("Intent")} />).with({
    tag: "Education"
  }, ({
    data
  }) => <Education intent={data.intent} onDone={bindOnDone("Education")} />).with({
    tag: "Resuming"
  }, () => <Resuming onDone={bindOnDone("Resuming")} />).with({
    tag: "Notifications"
  }, ({
    data
  }) => <Notifications intent={data.intent} onDone={bindOnDone("Notifications")} />).with({
    tag: "Complete"
  }, () => <FullscreenSpinner />).exhaustive();
  return (
    // flexBasis auto (web): in the phone page-scroll layout this wrapper
    // must size from its content so the pinned screen's 100dvh floor can
    // grow the page; `flex: 1`'s basis 0 would collapse it. In definite
    // parents (desktop shell, native) grow still fills as before.
    <YStack flex={1} position="relative" overflow="hidden" $platform-web={{
      flexBasis: "auto"
    }} data-sentry-element="YStack" data-sentry-component="OnboardingFlow" data-sentry-source-file="OnboardingFlow.tsx">
      <Transition stateKey={state.tag} direction={direction} data-sentry-element="Transition" data-sentry-source-file="OnboardingFlow.tsx">
        {screen}
      </Transition>
    </YStack>
  );
}